import { Helmet } from 'react-helmet-async';

import { extractRoutes, Router } from '@xing-com/crate-core-router';
import type { Host, Xinglet } from '@xing-com/crate-xinglet';
import { XingletLoader } from '@xing-com/crate-xinglet';
import type { Runtime } from '@xing-com/crate-xinglet/internal';
import { useRuntime, useRuntimeConfig } from '@xing-com/crate-xinglet/internal';
import GlobalStyles from '@xing-com/global-styles';
import { useScrambledId } from '@xing-com/scrambled-id';

import { NotFound } from './error-components';
import { LoggedInHub, LoggedOutHub } from './hubs';
import { Route } from './route';
import { useRegisterExperiments } from './use-register-experiments';

type RoutingProps = {
  baseSite?: string;
};

const SetScrambledId: React.FC = () => {
  useScrambledId();
  return null;
};

const Routing: React.FC<RoutingProps> = ({ baseSite = 'xing' }) => {
  const runtime = useRuntime();
  const routes = extractRoutes(runtime.manifestMap, baseSite);

  return (
    <>
      <Router>
        {routes.map((route) => {
          const path = route.path.replace(/\/\*splat$/, '/*');

          return <Route key="static" path={path} route={route} />;
        })}
        <NotFound default />
      </Router>
    </>
  );
};

const Shell: React.FC<React.PropsWithChildren<{ baseSite?: string }>> = ({
  baseSite,
  children,
}) => {
  useRegisterExperiments();

  const { loginState } = useRuntimeConfig();

  return (
    <GlobalStyles>
      {loginState === 'LOGGED_OUT' ? (
        <LoggedOutHub enableActivityCenter={baseSite !== 'lebenslauf'}>
          {children}
        </LoggedOutHub>
      ) : (
        <LoggedInHub loginState={loginState}>
          {children}
          <SetScrambledId />
        </LoggedInHub>
      )}
    </GlobalStyles>
  );
};

export default class RootXinglet implements Xinglet {
  public getComponent(
    host: Host,
    runtime: Runtime
  ): React.ComponentType<{
    basePath: string;
    baseSite?: string;
  }> {
    if (!host.isServer) {
      setTimeout(() => {
        // NOTE: We preload error pages here as it would be too late in case of
        // a network outage
        runtime.loadXinglet('@xing-com/crate-communication-error-pages');
      }, 1000);
    }

    return ({ basePath, baseSite }) => {
      const favicon = runtime.config.isPreview ? 'data:,' : '/favicon.ico';

      return (
        <XingletLoader
          name="@xing-com/crate-core-hops-environment"
          basePath={basePath}
        >
          <Helmet>
            <title>XING</title>
            <link rel="icon" href={favicon} />
          </Helmet>
          <Shell>
            <Routing baseSite={baseSite} />
          </Shell>
        </XingletLoader>
      );
    };
  }
}
